import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { ComparisonTable } from "@/components/content/ComparisonTable";
import { VerdictBox } from "@/components/content/VerdictBox";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";
import { NavLink } from "@/components/NavLink";

const WebsiteVsWebsitePlusGBP = () => {
  const comparisonData = [
    { feature: "Google Map Visibility", optionA: "None", optionB: "Map Pack ranking" },
    { feature: "\"Near Me\" Searches", optionA: "Limited", optionB: "Dominant" },
    { feature: "Mobile Click-to-Call", optionA: "Website only", optionB: "Maps + Website" },
    { feature: "Review Display", optionA: "Manual embed", optionB: "Automatic on Google" },
    { feature: "Local Trust Signals", optionA: "Website only", optionB: "Google verified" },
    { feature: "Lead Sources", optionA: "1 (website)", optionB: "2 (website + maps)" },
    { feature: "Total Lead Potential", optionA: "Baseline", optionB: "40-60% increase" },
    { feature: "Investment", optionA: "Website cost", optionB: "Website + $200-$500/mo" },
  ];

  const verdicts = [
    { label: "Local Visibility", recommendation: "Website + GBP" },
    { label: "Lead Volume", recommendation: "Website + GBP" },
    { label: "Tight Budget", recommendation: "Website first, add GBP later" },
    { label: "New Business", recommendation: "Both from day one" },
    { label: "Maximum ROI", recommendation: "Website + GBP optimization" },
  ];

  const faqs = [
    {
      question: "Do I need both a website and Google Business Profile?",
      answer: "Yes. Your website captures organic search traffic and builds credibility. Your Google Business Profile captures map searches and 'near me' queries. Together, they maximize your visibility and leads."
    },
    {
      question: "Can I just use Google Business Profile without a website?",
      answer: "Technically yes, but you're leaving leads on the table. GBP alone limits your ability to rank for service-specific keywords, showcase your work, and convert visitors with detailed information."
    },
    {
      question: "How does GBP optimization increase leads?",
      answer: "Optimized GBP profiles rank higher in map results, appear for more search terms, display prominently on mobile, and build trust through reviews. This typically increases leads by 40-60%."
    },
    {
      question: "What does Google Business Profile optimization include?",
      answer: "Complete profile setup, category optimization, service descriptions, photos, review generation strategy, Q&A management, post publishing, citation building, and ongoing monitoring."
    },
    {
      question: "How long until I see results from GBP optimization?",
      answer: "Initial improvements show within 2-4 weeks. Full optimization results typically appear in 1-3 months. Map pack rankings depend on competition, reviews, and citation consistency."
    },
    {
      question: "Is GBP optimization worth the monthly cost?",
      answer: "For most lawn care businesses, absolutely. Even one additional customer per month from improved map visibility can pay for the entire service many times over."
    },
    {
      question: "Can I optimize my Google Business Profile myself?",
      answer: "Basic setup, yes. But maximizing visibility requires expertise in categories, attributes, citation building, review management, and ongoing optimization most owners don't have time for."
    },
    {
      question: "How do reviews affect my GBP ranking?",
      answer: "Reviews are a major ranking factor. More reviews (with higher ratings) = better map rankings. A strong review strategy is essential for GBP success."
    },
    {
      question: "What's the difference between GBP and local SEO?",
      answer: "GBP optimization is one part of local SEO. Local SEO also includes website optimization, service area pages, local keywords, citations, and backlinks. They work together for maximum visibility."
    },
    {
      question: "Should I invest in GBP optimization or Google Ads first?",
      answer: "GBP optimization first. It's a lower ongoing cost and builds sustainable visibility. Add Google Ads for immediate lead volume once your GBP is optimized."
    },
  ];

  const CANONICAL = "https://www.leafrushmarketing.com/comparisons/website-vs-website-plus-gbp/";

  return (
    <>
      <SEO 
        title="Website Only vs Website + GBP for Lawn Care"
        description="Website only or website plus Google Business Profile? Compare lead generation potential and see why lawn care businesses need both."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
      />
      <PageSchema 
        title="Website Only vs Website + Google Business Profile"
        description="Website only or website plus Google Business Profile? Compare lead generation potential for lawn care businesses."
        url={CANONICAL}
        faqs={faqs}
      />
      
      <article className="py-16">
        <div className="container px-4 md:px-6">
          <LastUpdated />
          
          <header className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Website Only vs Website + GBP: Maximizing Your Local Visibility
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              A great website is just the start. Here's why pairing it with Google Business Profile optimization unlocks your full lead potential.
            </p>
          </header>

          <TopCTA />

          <div className="max-w-4xl mx-auto">
            <VerdictBox verdicts={verdicts} />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Lead Generation Comparison</h2>
              <p className="text-muted-foreground mb-6">
                Your <NavLink to="/lawn-care-website-design/" className="text-primary hover:underline">lawn care website</NavLink> captures 
                organic search traffic. <NavLink to="/growth-add-ons/gbp/" className="text-primary hover:underline">GBP optimization</NavLink> captures 
                map searches. Here's the difference:
              </p>
              <ComparisonTable 
                optionAName="Website Only"
                optionBName="Website + GBP"
                rows={comparisonData}
              />
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">What You Miss Without GBP</h2>
              <p className="text-muted-foreground mb-4">
                46% of all Google searches have local intent. Without optimized GBP, you're invisible for:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">✗</span>
                  <span>"Lawn care near me" and similar searches</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">✗</span>
                  <span>Google Maps searches in your service area</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">✗</span>
                  <span>Mobile users clicking "Call" from map results</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">✗</span>
                  <span>Customers checking reviews before calling</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">The Power of Combining Both</h2>
              <div className="bg-primary/10 p-6 rounded-lg mb-6">
                <h3 className="font-bold mb-3">Website + GBP = Complete Local Presence</h3>
                <ul className="space-y-2">
                  <li>• <strong>Website:</strong> Ranks for service keywords, showcases work, converts visitors</li>
                  <li>• <strong>GBP:</strong> Appears in map pack, captures "near me" searches, displays reviews</li>
                  <li>• <strong>Together:</strong> You appear in BOTH organic results AND map results</li>
                </ul>
              </div>
              <p className="text-muted-foreground">
                When customers search for lawn care in your area, they see you twice—in organic listings AND the map pack. 
                This dominance builds trust and captures more clicks.
              </p>
            </section>

            <MidPageCTA />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Our Complete Local Visibility Solution</h2>
              <p className="text-muted-foreground mb-4">
                Start with a <NavLink to="/websites/" className="text-primary hover:underline">professional lawn care website</NavLink> built 
                for conversions and <NavLink to="/lawn-care-website-seo/" className="text-primary hover:underline">SEO</NavLink>.
              </p>
              <p className="text-muted-foreground mb-4">
                Add <NavLink to="/growth-add-ons/gbp/" className="text-primary hover:underline">Google Business Profile optimization</NavLink> to 
                capture map searches and build your review presence.
              </p>
              <p className="text-muted-foreground mb-4">
                Want faster results? Layer in <NavLink to="/growth-add-ons/google-ads/" className="text-primary hover:underline">Google Ads</NavLink> while 
                your organic presence builds.
              </p>
              <p className="text-muted-foreground">
                The result: multiple lead sources working together for consistent customer flow.
              </p>
            </section>

            <FAQSection faqs={faqs} />

            <InternalLinks 
              links={[
                { href: "/comparisons/seo-website-vs-ads-first-lawn-care/", text: "SEO vs Google Ads" },
                { href: "/resources/google-business-profile-for-lawn-care/", text: "GBP Setup Guide" },
                { href: "/service-area-pages-local-seo/", text: "Service Area Pages" },
                { href: "/growth-add-ons/", text: "All Growth Add-Ons" },
              ]}
            />
          </div>
        </div>
      </article>

      <BottomCTA />
      
      <LeadFormSection title="Schedule Your Free Consultation" />
    </>
  );
};

export default WebsiteVsWebsitePlusGBP;
